/* @flow */

import * as React from 'react';
import styled from '@emotion/styled';
import classNames from 'classnames';
import useSheet from 'app/jss';
import { formatName } from 'app/utils/displayUtils';
import FlexBox from '../styles/FlexBox';
import ClosedEntityRow from './ClosedEntityRow';

type Props = {
  classes: Object,
  commonPlayerIds: Array<string>,
  commonTeamIds: Array<string>,
  currentWidgetId?: string,
  diffValue: boolean,
  displayOpponent?: boolean,
  displayUser?: boolean,
  displayWidget?: boolean,
  entities: Array<*>,
  isModal?: boolean,
  isRacing?: boolean,
  onWidgetClick: (id: string) => void,
  positionAbbr: string,
  compact?: boolean
};

const defaultProps = {
  currentWidgetId: undefined,
  displayOpponent: false,
  displayUser: true,
  displayWidget: false,
  isModal: false,
  isRacing: false,
  compact: false
};

const Cell = styled.div({
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1
});

function PerformanceSection({
  classes,
  commonPlayerIds,
  commonTeamIds,
  currentWidgetId,
  diffValue,
  displayOpponent,
  displayUser,
  displayWidget,
  entities,
  isModal,
  isRacing,
  onWidgetClick,
  positionAbbr,
  compact
}: Props) {
  const widgetEnabled = displayWidget && (!displayOpponent || !displayUser || isModal);
  const nameLengthLimit = 10;

  const commonIds = commonPlayerIds.concat(commonTeamIds);

  return (
    <div className={classes.section}>
      {entities.map(([mine, theirs = {}]) => (
        <FlexBox key={`${mine ? mine.idStr : ''}-${theirs ? theirs.idStr : ''}`}>
          {displayUser && (
            <Cell className={classNames(classes.outline, classes.box)}>
              {mine && (!diffValue || !commonIds.includes(mine.idStr)) && (
                <ClosedEntityRow
                  id={mine.id}
                  awayTeam={mine.awayAbbr}
                  bonus={mine.bonus}
                  displayWidget={widgetEnabled && !mine.isTeam}
                  dropped={mine.dropped}
                  gameOrder={mine.gameOrder}
                  gamePlaced={mine.gamePlaced}
                  homeTeam={mine.homeAbbr}
                  idStr={mine.idStr}
                  isHome={mine.isHome}
                  isTeam={mine.isTeam}
                  isRacing={isRacing}
                  isScratched={mine.isScratched}
                  labels={mine.labels}
                  name={compact ? formatName(mine.name, nameLengthLimit) : mine.name}
                  number={mine.number}
                  onWidgetClick={onWidgetClick}
                  popularity={mine.popularity}
                  position={positionAbbr}
                  positionId={mine.positionId}
                  progress={mine.progress}
                  reverseOrder={mine.reverseOrder}
                  rider={mine.rider}
                  riderImage={mine.riderImage}
                  score={mine.score}
                  showWidget={currentWidgetId === mine.idStr}
                  statusId={mine.statusId}
                  trainer={mine.trainer}
                  twin={displayOpponent && theirs && commonIds.includes(mine.idStr)}
                  twoUp={displayOpponent}
                  win={mine.win}
                  place={mine.place}
                />
              )}
            </Cell>
          )}
          {displayOpponent && (
            <Cell className={classNames(classes.outline, classes.box)}>
              {theirs && (!diffValue || !commonIds.includes(theirs.idStr)) && (
                <ClosedEntityRow
                  id={theirs.id}
                  awayTeam={theirs.awayAbbr}
                  bonus={theirs.bonus}
                  displayWidget={widgetEnabled && !theirs.isTeam}
                  dropped={theirs.dropped}
                  gameOrder={theirs.gameOrder}
                  gamePlaced={theirs.gamePlaced}
                  homeTeam={theirs.homeAbbr}
                  idStr={theirs.idStr}
                  isHome={theirs.isHome}
                  isRacing={isRacing}
                  isScratched={theirs.isScratched}
                  isTeam={theirs.isTeam}
                  labels={theirs.labels}
                  name={compact ? formatName(theirs.name, nameLengthLimit) : theirs.name}
                  number={theirs.number}
                  onWidgetClick={onWidgetClick}
                  popularity={theirs.popularity}
                  position={positionAbbr}
                  positionId={theirs.positionId}
                  progress={theirs.progress}
                  reverseOrder={theirs.reverseOrder}
                  rider={theirs.rider}
                  riderImage={theirs.riderImage}
                  score={theirs.score}
                  showWidget={currentWidgetId === theirs.idStr}
                  statusId={theirs.statusId}
                  trainer={theirs.trainer}
                  twin={mine && commonIds.includes(theirs.idStr)}
                  maskPlayer={!theirs.id}
                  win={theirs.win}
                  place={theirs.place}
                  twoUp
                />
              )}
            </Cell>
          )}
        </FlexBox>
      ))}
    </div>
  );
}

PerformanceSection.defaultProps = defaultProps;

function applyStyles(theme: Theme) {
  return {
    section: {
      backgroundColor: theme.color.white
    },
    outline: {
      border: `0 solid ${theme.color.basis6}`,
      '&:not(:first-of-type)': {
        borderLeftWidth: 1
      }
    },
    subtitle: {
      backgroundColor: theme.color.basis6,
      borderBottomWidth: 2,
      color: theme.color.basis1,
      fontSize: '0.75em',
      paddingRight: theme.spacing.small,
      paddingLeft: theme.spacing.small,
      textTransform: 'uppercase'
    },
    subtitleInner: {
      flexBasis: '100%',
      overflow: 'hidden',
      textAlign: 'center',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    box: {
      minWidth: 0 // flexbox hack
    }
  };
}

export default useSheet(applyStyles)(PerformanceSection);
